import { lazyAssets, LazyAsset } from '@sdk/lazy';
import { useRuntimeInfo } from '@sdk/react/hooks/useRuntimeInfo';
/**
 * useTerminalStripController — the terminal-strip CHROME (docs/tab-management.md
 * Part 3 §6). After the Tab-entity cutover the strip itself is the shared
 * `UnifiedTabStrip` (rows from the `tab` action, chips from `tab-row-item`), and
 * the terminal body is `TabbedTerminal`; both derive order/label/active from the
 * one backend source. This hook owns only the chrome neither of them computes:
 *
 *   - spawn flows (claude/codex/copilot/terminal/sandbox) + harness gating
 *   - the opener toolbar (`trailing`), the new-tab menu, and the empty-state
 *     spawn handlers
 *   - the history / resume / install modals
 *
 * It holds NO session list, active-key, or close/rename/select handlers — those
 * are URL-first in `UnifiedTabStrip`. Nor any project switcher or history
 * controls: both live in the top navigation bar (`RuntimeChip`), the app's one
 * browser-style chrome, so the strip's band is all tabs. Spawns navigate directly to the new
 * terminal (no host callback round-trip).
 */
import {
  AgenticProcess,
  CapabilityKinds,
  ContextEntitiesEnum,
  InstallState,
  dataContext,
  GraphContext,
  ViewType,
  type ComputeNode,
} from '@sdk';
import { useHarnessAvailability } from '@src/components/workers/harness-availability';
import { harnessStatus } from '@src/components/status/use-status-record';
import { useIsAdvanced, ViewMode } from '@src/contexts/view-mode-context';
import { DockPointer } from '@src/navigation/DockPointer';
import { projectScope } from '@src/lib/scope-filter';
import { InputDialog } from '@src/components/ui/input-dialog';
import { type TabStripContextMenuItem } from '@src/components/tabs/TabStrip';
import { useResumeInTerminal } from '@src/hooks/use-resume-in-terminal';
import { notify } from '@src/notifications';
import { PROVIDER_META } from '@src/tabs/provider-meta';
import { useDockNavigation } from '@src/navigation/useDockNavigation';
import { openNewChat } from '@src/navigation/open-new-chat';
import { Cloud, History, Sparkles, SquareTerminal } from 'lucide-react';
import { FlowIcon } from '@sdk/react/FlowIcon';
import { useContext } from '@sdk/react/hooks';
import { iconForType } from '@src/components/graph-view/icons/iconRegistry';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useLingui } from '@lingui/react/macro';
import { HistoryModal } from '@src/components/terminal/HistoryModal';
import { useHarnessInstallPrompt } from '@src/components/terminal/openers/use-harness-install-prompt';
import { TerminalOpenerToolbar } from '@src/components/terminal/openers/TerminalOpenerToolbar';
import type { OpenerDescriptor } from '@src/components/terminal/openers/tab_opener_types';
import { HARNESS_CAPABILITY_BY_WORKER, type WorkerType } from '@src/components/workers/worker-types';

/** Claude, badged to say this opener RESUMES a session rather than starting one.
 *
 *  The badge is composed, not drawn: `brands.claude.restore` is the same icon
 *  with a `restore` role, so a fifth vendor's resume glyph is a pack entry
 *  rather than another hand-rolled span. */
const ClaudeResumeIcon: React.FC<{ className?: string }> = ({ className }) => (
  <FlowIcon
    icon="brands.claude"
    role="restore"
    className={`h-4 w-4 text-orange-500 ${className ?? ''}`}
    badgeClassName="text-foreground/80"
  />
);

export interface TerminalStripControllerOptions {
  /** Whether to expose the "Add Tab" opener toolbar as `trailing`. */
  addTabButton?: boolean;
  /** Pin spawned shells/processes to this project (else the active project). */
  spawnProjectId?: string | null;
}

export interface TerminalStripController {
  /** The project the chrome is scoped to (strip list scope). */
  tabsProjectId: string | null;
  newTabMenuItems: TabStripContextMenuItem[];
  closeShortcutLabel: string;
  /** Trailing opener toolbar (null unless `addTabButton`). */
  trailing: React.ReactNode;
  /**
   * The spawn openers as descriptors (claude/codex/copilot/terminal/sandbox/
   * history/…). Exposed so a surface can render a *subset* itself —
   * e.g. the project home's launcher takes only `terminal` — instead of
   * re-deriving a button's label/icon/pending state from the raw handlers.
   */
  openers: OpenerDescriptor[];
  /** History / resume / install dialogs — render once in the host. */
  modals: React.ReactNode;
  isTabCreationPending: boolean;
  isClaudeCreationPending: boolean;
  isTerminalCreationPending: boolean;
  handleStartClaude: () => Promise<void> | void;
  handleStartTerminal: () => Promise<void> | void;
  handleOpenHistory: () => void;
}

export function useTerminalStripController({
  addTabButton,
  spawnProjectId,
}: TerminalStripControllerOptions = {}): TerminalStripController {
  const { t } = useLingui();
  const { navigation } = useDockNavigation();
  useRuntimeInfo();
  const { sandboxComputeNode } = useContext();
  const isAdvanced = useIsAdvanced();
  // Per-type icon from the backend TypeInfo registry (never hardcode a glyph).
  // Memoized so it stays referentially stable and doesn't churn the openers /
  // newTabMenuItems memos on every render.
  const ContextIcon = useMemo(() => iconForType(GraphContext.type), []);
  const tabsProjectId = spawnProjectId ?? dataContext.project?.id ?? null;

  const tabCreationLockRef = useRef(false);
  const [pendingTabCreation, setPendingTabCreation] = useState<
    'claude' | 'codex' | 'copilot' | 'opencode' | 'terminal' | null
  >(null);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [resumeByIdOpen, setResumeByIdOpen] = useState(false);
  const { warnings, record } = useHarnessAvailability();
  // Shared with the vibe chat's first prompt — the other route that discovers a
  // missing harness (see use-harness-install-prompt).
  const { promptToInstall, confirmMissingThen, dialog: installDialog } = useHarnessInstallPrompt();
  const { resumeInTerminal } = useResumeInTerminal();

  const clearPending = useCallback(() => {
    tabCreationLockRef.current = false;
    setPendingTabCreation(null);
  }, []);

  // "Start <vendor>" — create the AgenticProcess then navigate to its terminal.
  const startAgenticTab = useCallback(
    async (kind: 'claude' | 'codex' | 'copilot' | 'opencode', workerType?: WorkerType, mode?: ViewMode) => {
      if (tabCreationLockRef.current) return;
      tabCreationLockRef.current = true;
      setPendingTabCreation(kind);
      // Gate on the vendor the user actually clicked, else the default harness — read off the
      // status record, so a missing binary is reported against the right one.
      const requiredKind = workerType ? HARNESS_CAPABILITY_BY_WORKER[workerType] : CapabilityKinds.Harness;
      // The lock is released in `finally` and NOWHERE else: an unhandled throw
      // used to strand it set, which left a permanent spinner on the opener and
      // made every later click a silent no-op until the page reloaded.
      try {
        // Status not read yet (no record) fails open — it never blocks tab creation.
        const kind = workerType ? requiredKind : record?.default_harness;
        if (kind && harnessStatus(record, kind)?.install === InstallState.NotInstalled) {
          promptToInstall();
          return;
        }
        // openNewChat creates AND navigates — it owns the chat-mode propagation,
        // so a second openShellProcess here would re-navigate the same dock
        // without `?viewMode` and strip the mode back off the URL.
        // The strip's openers start a terminal (PTY) session, whatever mode is
        // on screen — a tab beside the current one, never another Vibe ("New
        // Vibe" is its own opener). Only `launchIn` (the quick-launch slot)
        // names another shape.
        await openNewChat(navigation, {
          ...(spawnProjectId ? { projectId: spawnProjectId } : {}),
          ...(workerType ? { workerType } : {}),
          mode: mode ?? ViewMode.Advanced,
        });
      } catch (error: unknown) {
        // The spawn failed. ASK what is wrong rather than assuming, because the
        // two likely causes want different screens and the pre-flight cannot
        // tell them apart: it reads the capability row, which is what let us
        // through here.
        //
        // A harness gone from disk gets the install dialog. A harness that is
        // PRESENT but has nothing to run on — no sign-in, no key, no budget —
        // gets the sign-in modal; sending that one to the Capabilities view (an
        // inventory of what is installed) offered a re-check and an installer
        // for something already installed, which is what a real Windows report
        // looked like. Anything else keeps the Capabilities view rather than
        // being mislabelled as either.
        confirmMissingThen(requiredKind, error, () =>
          navigation.openTab(ViewType.CAPABILITIES, { capabilityKind: requiredKind }),
        );
      } finally {
        clearPending();
      }
    },
    [confirmMissingThen, promptToInstall, clearPending, navigation, spawnProjectId, record],
  );

  const handleStartClaude = useCallback(() => startAgenticTab('claude', 'claude_code'), [startAgenticTab]);
  const handleStartCodex = useCallback(() => startAgenticTab('codex', 'codex'), [startAgenticTab]);
  const handleStartCopilot = useCallback(() => startAgenticTab('copilot', 'copilot'), [startAgenticTab]);
  const handleStartOpenCode = useCallback(() => startAgenticTab('opencode', 'opencode'), [startAgenticTab]);

  const startTerminalTab = useCallback(
    async (computeNode?: ComputeNode) => {
      if (tabCreationLockRef.current) return;
      tabCreationLockRef.current = true;
      setPendingTabCreation('terminal');
      const result = await navigation.openNewShell({
        ...(computeNode ? { computeNode } : {}),
        ...(spawnProjectId ? { projectId: spawnProjectId } : {}),
        skipNavigate: true,
      });
      if (!result?.shellId) {
        clearPending();
        return;
      }
      // A top-level tab, never a child of the Vibe tab on screen.
      await navigation.openShell(result.shellId, { topLevel: true });
      clearPending();
    },
    [clearPending, navigation, spawnProjectId],
  );

  const handleStartTerminal = useCallback(() => startTerminalTab(), [startTerminalTab]);
  // "New Vibe": the tabless Vibe home, whose prompt opens the next Vibe tab.
  const handleNewVibe = useCallback(() => {
    const home = DockPointer.root().withViewMode(ViewMode.Vibe);
    return navigation.openDock(spawnProjectId ? home.withScopeFilter(projectScope(spawnProjectId)) : home);
  }, [navigation, spawnProjectId]);

  // "Open Context" — freeze the current global context (the ContextEntitiesEnum
  // slots) into a new GraphContext entity and open it in a tab. URL-first: we
  // create the entity then navigate; no optimistic context writes.
  const handleOpenContext = useCallback(async () => {
    const slotMap: Record<string, string> = {};
    const typeids: string[] = [];
    for (const key of Object.values(ContextEntitiesEnum)) {
      const tid = dataContext.getContextEntityTypeId(key);
      if (!tid) continue;
      const s = tid.toString();
      slotMap[key] = s;
      typeids.push(s);
    }
    const scope = dataContext.project?.typeId ? [dataContext.project.typeId] : [];
    const gc = new GraphContext({});
    gc.context_typeids = typeids;
    gc.slot_map = slotMap;
    gc.name = `Context ${gc.id.slice(0, 8)}`;
    try {
      await gc.save(scope);
    } catch (e) {
      // Surface the failure instead of silently doing nothing — the most common
      // cause is a backend that predates the graph_context type (needs restart).
      notify.error({
        title: t`Could not freeze context`,
        message: e instanceof Error ? e.message : t`Failed to save the context snapshot.`,
      });
      return;
    }
    navigation.openDock(DockPointer.forGraphContext(gc.typeId.id));
  }, [navigation]);

  const handleStartSandbox = useCallback(async () => {
    await lazyAssets.load(LazyAsset.RuntimeInfo);
    const sandboxNode = dataContext.sandboxComputeNode;
    if (!sandboxNode) return;
    return startTerminalTab(sandboxNode);
  }, [startTerminalTab]);

  // Use Ctrl key on Mac, Win key on Windows, Alt key on Linux (label only).
  const osPlatform: string =
    (navigator as Navigator & { userAgentData?: { platform: string } }).userAgentData?.platform ?? navigator.userAgent;
  const modLabel = /Mac/i.test(osPlatform) ? 'Ctrl' : /Win/i.test(osPlatform) ? 'Win' : 'Alt';

  const isTabCreationPending = pendingTabCreation !== null;
  const isClaudeCreationPending = pendingTabCreation === 'claude';
  const isCodexCreationPending = pendingTabCreation === 'codex';
  const isCopilotCreationPending = pendingTabCreation === 'copilot';
  const isOpenCodeCreationPending = pendingTabCreation === 'opencode';
  const isTerminalCreationPending = pendingTabCreation === 'terminal';
  const sandboxAvailable = !!sandboxComputeNode;

  const openers = useMemo<OpenerDescriptor[]>(
    () => [
      {
        // The tabless Vibe home: sending its prompt opens the next Vibe tab.
        id: 'vibe',
        label: t`New Vibe`,
        Icon: Sparkles,
        onActivate: handleNewVibe,
        available: true,
        disabled: isTabCreationPending,
      },
      {
        id: 'claude',
        label: t`Start Claude`,
        Icon: PROVIDER_META.claude.Icon,
        iconClassName: PROVIDER_META.claude.iconClassName,
        onActivate: () => void handleStartClaude(),
        launchIn: (mode) => void startAgenticTab('claude', 'claude_code', mode),
        available: true,
        warning: warnings.claude_code,
        capabilityKind: HARNESS_CAPABILITY_BY_WORKER.claude_code,
        pendingInline: isClaudeCreationPending,
        disabled: isTabCreationPending,
      },
      {
        id: 'codex',
        label: t`Start Codex`,
        Icon: PROVIDER_META.codex.Icon,
        iconClassName: PROVIDER_META.codex.iconClassName,
        onActivate: () => void handleStartCodex(),
        launchIn: (mode) => void startAgenticTab('codex', 'codex', mode),
        available: true,
        warning: warnings.codex,
        capabilityKind: HARNESS_CAPABILITY_BY_WORKER.codex,
        pendingInline: isCodexCreationPending,
        disabled: isTabCreationPending,
      },
      {
        id: 'copilot',
        label: t`Start Copilot`,
        Icon: PROVIDER_META.copilot.Icon,
        iconClassName: PROVIDER_META.copilot.iconClassName,
        onActivate: () => void handleStartCopilot(),
        launchIn: (mode) => void startAgenticTab('copilot', 'copilot', mode),
        available: true,
        warning: warnings.copilot,
        capabilityKind: HARNESS_CAPABILITY_BY_WORKER.copilot,
        pendingInline: isCopilotCreationPending,
        disabled: isTabCreationPending,
      },
      {
        id: 'opencode',
        label: t`Start OpenCode`,
        Icon: PROVIDER_META.opencode.Icon,
        iconClassName: PROVIDER_META.opencode.iconClassName,
        onActivate: () => void handleStartOpenCode(),
        launchIn: (mode) => void startAgenticTab('opencode', 'opencode', mode),
        available: true,
        warning: warnings.opencode,
        capabilityKind: HARNESS_CAPABILITY_BY_WORKER.opencode,
        pendingInline: isOpenCodeCreationPending,
        disabled: isTabCreationPending,
      },
      {
        id: 'claude-resume-by-id',
        label: t`Resume Claude session…`,
        Icon: ClaudeResumeIcon,
        onActivate: () => setResumeByIdOpen(true),
        available: true,
        disabled: isTabCreationPending,
      },
      {
        id: 'terminal',
        label: t`Open terminal (${modLabel}+T)`,
        Icon: SquareTerminal,
        onActivate: () => void handleStartTerminal(),
        available: true,
        pendingInline: isTerminalCreationPending,
        disabled: isTabCreationPending,
      },
      {
        id: 'sandbox',
        label: t`Open sandbox terminal (E2B)`,
        Icon: Cloud,
        iconClassName: 'text-sky-500',
        onActivate: () => void handleStartSandbox(),
        available: sandboxAvailable,
        pendingInline: isTerminalCreationPending,
        disabled: isTabCreationPending,
      },
      {
        id: 'history',
        label: t`Open from history`,
        Icon: History,
        onActivate: () => setHistoryModalOpen(true),
        available: true,
      },
      {
        // Advanced-only: freeze the current global context into a GraphContext.
        id: 'open-context',
        label: t`Open Context`,
        Icon: ContextIcon,
        onActivate: () => void handleOpenContext(),
        available: isAdvanced,
        disabled: isTabCreationPending,
      },
    ],
    [
      modLabel,
      handleNewVibe,
      startAgenticTab,
      handleStartClaude,
      handleStartCodex,
      handleStartCopilot,
      handleStartOpenCode,
      handleStartTerminal,
      handleStartSandbox,
      handleOpenContext,
      isAdvanced,
      ContextIcon,
      sandboxAvailable,
      warnings,
      isClaudeCreationPending,
      isCodexCreationPending,
      isCopilotCreationPending,
      isOpenCodeCreationPending,
      isTerminalCreationPending,
      isTabCreationPending,
    ],
  );

  const trailing = useMemo(
    () =>
      addTabButton ? <TerminalOpenerToolbar openers={openers} isTabCreationPending={isTabCreationPending} /> : null,
    [addTabButton, openers, isTabCreationPending],
  );

  const newTabMenuItems = useMemo<TabStripContextMenuItem[]>(
    () => [
      { label: t`New Vibe`, onSelect: handleNewVibe },
      { label: t`New Claude Session`, onSelect: () => void handleStartClaude() },
      { label: t`New Terminal`, shortcut: `${modLabel}+T`, onSelect: () => void handleStartTerminal() },
      // Advanced-only: freeze the current context into a GraphContext and open it.
      ...(isAdvanced ? [{ label: t`Open Context`, Icon: ContextIcon, onSelect: () => void handleOpenContext() }] : []),
    ],
    [modLabel, handleNewVibe, handleStartClaude, handleStartTerminal, isAdvanced, handleOpenContext, ContextIcon],
  );

  const modals = (
    <>
      {installDialog}
      <HistoryModal
        open={historyModalOpen}
        onOpenChange={setHistoryModalOpen}
        onSelect={(entry) => {
          void (async () => {
            setHistoryModalOpen(false);
            try {
              let processId: string | null = entry.agentic_process_id;
              if (!processId) {
                const process = await AgenticProcess.getByWorkerId(entry.worker_id);
                processId = process?.id ?? null;
              }
              if (!processId) {
                notify.error({
                  title: t`Session not found`,
                  message: t`Session ${entry.worker_id} is not in Claude, Codex, or Copilot history.`,
                  id: `session-not-found:${entry.worker_id}`,
                });
                return;
              }
              // No mode: the session reopens in the one it was last shown in (`last_mode`).
              await navigation.openShellProcess(processId);
            } catch (err) {
              console.error('[TabbedTerminal] Failed to open session from history:', err);
            }
          })();
        }}
      />
      <InputDialog
        open={resumeByIdOpen}
        onOpenChange={setResumeByIdOpen}
        title={t`Resume Claude session`}
        description={t`Paste a Claude CLI session id (UUID) to resume it in a new tab.`}
        placeholder={t`e.g. 0fa1a8c2-7b1d-4d6c-9d4e-b3e6c2f1d8aa`}
        confirmLabel={t`Resume`}
        onConfirm={(sessionId) =>
          resumeInTerminal(sessionId, undefined, undefined, undefined, { viewMode: ViewMode.Advanced })
        }
      />
    </>
  );

  // Every opener starts a terminal or a local session: none can run where terminals can't (the hub).
  const canOpenTabs = dataContext.caps.terminals;

  return {
    tabsProjectId,
    newTabMenuItems: canOpenTabs ? newTabMenuItems : [],
    closeShortcutLabel: `${modLabel}+W`,
    trailing: canOpenTabs ? trailing : null,
    openers,
    modals,
    isTabCreationPending,
    isClaudeCreationPending,
    isTerminalCreationPending,
    handleStartClaude,
    handleStartTerminal,
    handleOpenHistory: () => setHistoryModalOpen(true),
  };
}
